import { FC, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { Text, Flex } from "@radix-ui/themes";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";

import { atlasRoutes } from "@/app/router/routes";
import { Button } from "@/components/button";
import { LookupByUuidForm } from "@/components/forms/lookup-by-uuid-form";
import { api } from "@/modules/api";
import { NoteSummarySchema } from "@/domains/openapi/types";
import { NotesSearchForm } from "@/pages/notes/root/notes-search-form";
import { NotesTable } from "@/pages/notes/root/notes-table";

const PAGE_SIZE = 50;

type NotesSearchParams = {
  query: string;
  spaceAccountId: string;
  includeTrashed: boolean;
  includeSoftDeleted: boolean;
};

export const NotesRootPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialQuery = searchParams.get("query") ?? "";
  const initialSpaceAccountId = searchParams.get("spaceAccountId") ?? "";
  const initialIncludeTrashed = searchParams.get("includeTrashed") === "true";
  const initialIncludeSoftDeleted = searchParams.get("includeSoftDeleted") === "true";

  const [queryFilter, setQueryFilter] = useState<string>(initialQuery);
  const [spaceAccountFilter, setSpaceAccountFilter] = useState<string>(initialSpaceAccountId);
  const [includeTrashedFilter, setIncludeTrashedFilter] = useState<boolean>(initialIncludeTrashed);
  const [includeSoftDeletedFilter, setIncludeSoftDeletedFilter] =
    useState<boolean>(initialIncludeSoftDeleted);

  useEffect(() => {
    const query = searchParams.get("query") ?? "";
    const spaceAccountId = searchParams.get("spaceAccountId") ?? "";
    const includeTrashed = searchParams.get("includeTrashed") === "true";
    const includeSoftDeleted = searchParams.get("includeSoftDeleted") === "true";
    setQueryFilter(query);
    setSpaceAccountFilter(spaceAccountId);
    setIncludeTrashedFilter(includeTrashed);
    setIncludeSoftDeletedFilter(includeSoftDeleted);
  }, [searchParams]);

  const trimmedQuery = queryFilter.trim();
  const trimmedSpaceAccount = spaceAccountFilter.trim();
  const hasFilters = trimmedQuery.length > 0 || trimmedSpaceAccount.length > 0;

  const { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [
        "notes-search",
        trimmedQuery,
        trimmedSpaceAccount,
        includeTrashedFilter,
        includeSoftDeletedFilter,
      ],
      queryFn: ({ pageParam = 0 }) =>
        api.post("/internal/v1/notes/search", {
          body: {
            query: trimmedQuery || undefined,
            filter_by_space_account_id: trimmedSpaceAccount || undefined,
            limit: PAGE_SIZE,
            offset: pageParam,
            include_trashed: includeTrashedFilter || undefined,
            include_soft_deleted: includeSoftDeletedFilter || undefined,
          },
        }),
      enabled: hasFilters,
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const lastTotal = lastPage.data?.total ?? 0;
        const loaded = allPages.reduce((acc, page) => acc + (page.data?.results?.length ?? 0), 0);
        return loaded < lastTotal ? loaded : undefined;
      },
      staleTime: 5 * 60 * 1000,
      retry: 1,
    });

  const handleSearch = ({
    query,
    spaceAccountId,
    includeTrashed,
    includeSoftDeleted,
  }: NotesSearchParams) => {
    const next = new URLSearchParams(searchParams);

    if (query) {
      next.set("query", query);
    } else {
      next.delete("query");
    }

    if (spaceAccountId) {
      next.set("spaceAccountId", spaceAccountId);
    } else {
      next.delete("spaceAccountId");
    }

    if (includeTrashed) {
      next.set("includeTrashed", "true");
    } else {
      next.delete("includeTrashed");
    }

    if (includeSoftDeleted) {
      next.set("includeSoftDeleted", "true");
    } else {
      next.delete("includeSoftDeleted");
    }

    setSearchParams(next);
  };

  const handleViewNote = (noteId: string) => {
    navigate(atlasRoutes.notesView({ params: { noteId } }).path);
  };

  const pages = data?.pages ?? [];
  const tableData = useMemo<NoteSummarySchema[]>(
    () => pages.flatMap(page => page?.data?.results ?? []),
    [pages]
  );
  const hasMoreResults = hasFilters && Boolean(hasNextPage);

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const errorMessage =
    error && error instanceof Error ? error.message : isError ? "Error fetching notes." : null;
  const noResults = hasFilters && !isLoading && tableData.length === 0;

  return (
    <Container>
      <Flex justify="between" align="center" mb="4">
        <h1>Notes</h1>
        <Button onClick={() => navigate(atlasRoutes.notesCreate({}).path)}>Create Note</Button>
      </Flex>
      <DescriptionText>
        Lookup a note directly by UUID or search by label and optional space account filter.
      </DescriptionText>

      <Section>
        <SectionTitle>Lookup By UUID</SectionTitle>
        <LookupByUuidForm
          onSubmit={uuid => navigate(atlasRoutes.notesView({ params: { noteId: uuid } }).path)}
        />
      </Section>

      <Section>
        <SectionTitle>Search Notes</SectionTitle>
        <NotesSearchForm
          initialQuery={queryFilter}
          initialSpaceAccountId={spaceAccountFilter}
          initialIncludeTrashed={includeTrashedFilter}
          initialIncludeSoftDeleted={includeSoftDeletedFilter}
          onSearch={handleSearch}
        />
        {!hasFilters && <HintText>Enter a query or space account ID to search.</HintText>}
        {isLoading && hasFilters && <div>Loading...</div>}
        {errorMessage && <div>{errorMessage}</div>}

        {tableData.length > 0 && hasFilters && (
          <NotesTable
            data={tableData}
            pageSize={PAGE_SIZE}
            onViewNote={handleViewNote}
            onLoadMore={handleLoadMore}
            showLoadMore={hasMoreResults}
            loadMoreLabel={isFetchingNextPage ? "Loading..." : "Load More"}
            isLoadMoreDisabled={isFetchingNextPage}
          />
        )}
        {noResults && <div>No notes found.</div>}
      </Section>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const DescriptionText = styled(Text)`
  display: block;
  color: var(--gray-12);
  margin-bottom: 1rem;
`;

const Section = styled.section`
  margin-top: 32px;
`;

const SectionTitle = styled(Text)`
  display: block;
  font-weight: 600;
  margin-bottom: 12px;
`;

const HintText = styled(Text)`
  display: block;
  color: var(--gray-11);
  margin-bottom: 0.5rem;
`;
